import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, ThumbsUp, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserStories = ({ userId }) => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/upvote`);
      setStories(stories.map(s => s.id === storyId ? { ...s, upvotes: s.upvotes + 1 } : s));
    } catch (error) {
      console.error('Error upvoting story:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading stories...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-container p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#1A1A1A] dark:text-[#FFFFFE]">
              Community Stories
            </h1>
          </div>
          <Button
            onClick={() => navigate('/share-story')}
            className="bg-[#CCFF00] text-black font-bold px-6 py-3 rounded-full hover:scale-105 transition-transform"
            data-testid="share-story-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Share Your Story
          </Button>
        </div>

        <div className="mb-6 bg-gradient-to-r from-[#CCFF00]/20 to-[#FF0080]/20 rounded-3xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#CCFF00] rounded-full flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-black" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">
                Real Stories, Real Impact
              </h3>
              <p className="text-[#5F6C7B] dark:text-[#94A1B2] leading-relaxed">
                Read experiences from peers who've navigated challenges with substances, mental health, and safe choices. Every story shared here helps someone feel less alone.
              </p>
            </div>
          </div>
        </div>

        {stories.length === 0 ? (
          <div className="bg-white dark:bg-[#232129] rounded-3xl p-12 text-center">
            <Heart className="w-16 h-16 mx-auto mb-4 text-[#5F6C7B]" />
            <h3 className="text-2xl font-bold mb-2 text-[#1A1A1A] dark:text-[#FFFFFE]">
              No Stories Yet
            </h3>
            <p className="text-[#5F6C7B] dark:text-[#94A1B2] mb-6">
              Be the first to share your experience with the community
            </p>
            <Button
              onClick={() => navigate('/share-story')}
              className="bg-[#CCFF00] text-black font-bold px-8 py-4 rounded-full hover:scale-105 transition-transform"
            >
              Share Your Story
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-[#232129] rounded-3xl p-6 border border-black/5 dark:border-white/5 hover:border-[#CCFF00] transition-colors"
                data-testid={`user-story-${story.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                    {story.category}
                  </span>
                  <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                    {story.author}
                  </span>
                </div>
                
                <h3 className="text-2xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-4">
                  {story.title}
                </h3>
                
                <p className="text-[#5F6C7B] dark:text-[#94A1B2] leading-relaxed mb-6 line-clamp-6">
                  {story.content}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/5">
                  <button
                    onClick={() => handleUpvote(story.id)}
                    className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2] hover:text-[#CCFF00] transition-colors"
                    data-testid={`upvote-story-${story.id}`}
                  >
                    <ThumbsUp className="w-5 h-5" />
                    <span className="font-medium">{story.upvotes}</span>
                  </button>
                  <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                    {new Date(story.timestamp).toLocaleDateString()}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserStories;
